import { useEffect, useState } from "react";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { GRUPO_LABEL, ACCION_LABEL, ACCION_ORDER, NON_EDITABLE_ROLES } from "@/lib/rbac";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export function MatrizPermisos() {
  const { user, meta } = useAuth();
  const [data, setData] = useState(null);
  const [role, setRole] = useState("");
  const [saving, setSaving] = useState("");
  // ago-2026 — El desplegable ya no ofrece los 21 roles de la plataforma a cualquier
  // entidad: se acota al catálogo del RÉGIMEN (`GET /api/roles-disponibles`), igual que el
  // diálogo de invitar usuario. En actividad vulnerable quedan los dos roles del vertical
  // más los heredados que la entidad ya tenga asignados.
  const [rolesRegimen, setRolesRegimen] = useState(null);

  const canManage = ["admin_entidad", "super_admin"].includes(user?.role);
  const roleLabel = (id) =>
    meta?.roles_labels?.[id] || (meta?.roles || []).find((r) => r.id === id)?.label || id;

  useEffect(() => {
    api.get("/roles-disponibles")
      .then((r) => setRolesRegimen([...(r.data?.roles || []), ...(r.data?.roles_en_uso || [])].map((x) => x.id)))
      .catch(() => setRolesRegimen(null));
  }, []);

  useEffect(() => {
    api.get("/rbac/matriz").then((r) => {
      setData(r.data);
    }).catch(() => {});
  }, []);

  const rolesMatriz = Object.keys(data?.matriz || {}).filter(
    (r) => !rolesRegimen || rolesRegimen.includes(r)
  );

  useEffect(() => {
    if (!data || role) return;
    const first = rolesMatriz.find((x) => !NON_EDITABLE_ROLES.has(x)) || rolesMatriz[0] || "";
    setRole(first);
  }, [data, rolesRegimen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Cargando matriz…</div>;

  const editable = canManage && !NON_EDITABLE_ROLES.has(role);
  const matrizRol = data.matriz[role] || {};

  const toggle = async (grupo, accion) => {
    if (!editable) return;
    const actuales = new Set(matrizRol[grupo] || []);
    if (actuales.has(accion)) actuales.delete(accion); else actuales.add(accion);
    const nuevas = ACCION_ORDER.filter((a) => actuales.has(a));
    // optimista
    setData((p) => ({ ...p, matriz: { ...p.matriz, [role]: { ...p.matriz[role], [grupo]: nuevas } } }));
    setSaving(`${grupo}`);
    try {
      await api.put("/rbac/matriz", { role, grupo, acciones: nuevas });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el cambio");
      setData((p) => ({ ...p, matriz: { ...p.matriz, [role]: { ...p.matriz[role], [grupo]: [...(matrizRol[grupo] || [])] } } }));
    } finally {
      setSaving("");
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
        <h3 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-amber-800">
          <Lock className="h-4 w-4" /> Reglas de segregación bloqueadas (no editables por nadie)
        </h3>
        <ul className="grid grid-cols-1 gap-1.5 text-sm text-amber-900/90 sm:grid-cols-2">
          {(data.reglas_bloqueadas || []).map((t) => (
            <li key={t} data-testid="regla-bloqueada" className="flex items-start gap-2">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" /> {t}
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-700">
            <ShieldCheck className="h-4 w-4 text-primary" /> Matriz de permisos por rol
          </h3>
          <div className="w-full sm:w-72">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="matriz-role-select"><SelectValue placeholder="Selecciona un rol" /></SelectTrigger>
              <SelectContent>
                {rolesMatriz.map((r) => (
                  <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!editable && (
          <div data-testid="matriz-readonly-banner" className="mb-3 flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-600">
            <Lock className="h-3.5 w-3.5" />
            {NON_EDITABLE_ROLES.has(role)
              ? "Este rol no es editable (regla de segregación bloqueada o solo lectura)."
              : "Solo el administrador de la entidad puede editar la matriz."}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm" data-testid="matriz-permisos-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Módulo</th>
                {ACCION_ORDER.map((a) => (
                  <th key={a} className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">{ACCION_LABEL[a]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.keys(GRUPO_LABEL).map((grupo) => {
                const acciones = new Set(matrizRol[grupo] || []);
                const sinAcceso = acciones.size === 0;
                return (
                  <tr key={grupo} className="border-b border-slate-100">
                    <td className="px-4 py-2.5 text-slate-700">
                      {GRUPO_LABEL[grupo]}
                      {sinAcceso && <Badge variant="secondary" className="ml-2 text-[10px]">sin acceso</Badge>}
                    </td>
                    {ACCION_ORDER.map((a) => (
                      <td key={a} className="px-4 py-2.5 text-center">
                        <Checkbox
                          data-testid={`perm-${role}-${grupo}-${a}`}
                          checked={acciones.has(a)}
                          disabled={!editable || saving === grupo}
                          onCheckedChange={() => toggle(grupo, a)}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-400">
          Los cambios se guardan automáticamente y solo aplican dentro de tu entidad. Las acciones reservadas
          (cerrar casos PLD críticos, confirmar coincidencias y autorizar overrides) son exclusivas del Oficial
          de Cumplimiento y no se modifican aquí.
        </p>
      </div>
    </div>
  );
}
